import {
  ArrowRightOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  ReloadOutlined
} from "@ant-design/icons"
import { Button, Card, Empty, Flex, Skeleton, Space } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../ActionTooltip.tsx"
import { neutral, shadows, widgetActionGap } from "./tokens"
import type {
  Capability,
  ExportFormat,
  WidgetPayload,
  WidgetState
} from "./types"
import { exportWidgetCsv } from "./widgetCsv"

export interface WidgetFrameProps {
  title?: string
  state?: WidgetState
  capabilities?: Capability[]
  widgetId?: string
  bare?: boolean
  emptyText?: string
  errorText?: string
  drillTarget?: string
  payload?: WidgetPayload | null
  onRefresh?: (id: string) => void
  onExpand?: (id: string) => void
  onExport?: (id: string, format: ExportFormat) => void
  onDrill?: (target: string) => void
  onRetry?: () => void
  /** Edit mode's drag handle / remove icon, shown beside the frame's own chrome. */
  editChrome?: React.ReactNode
  children?: React.ReactNode
}

const frameStyle: React.CSSProperties = {
  border: `1px solid ${neutral.border}`,
  borderRadius: 12,
  boxShadow: shadows.card
}

const FrameActions = ({
  capabilities,
  widgetId,
  title,
  drillTarget,
  payload,
  onRefresh,
  onExpand,
  onExport,
  onDrill
}: Pick<
  WidgetFrameProps,
  | "capabilities"
  | "widgetId"
  | "title"
  | "drillTarget"
  | "payload"
  | "onRefresh"
  | "onExpand"
  | "onExport"
  | "onDrill"
>) => {
  const { t } = useTranslation()
  const caps = capabilities ?? []
  const id = widgetId ?? ""
  if (caps.length === 0) return null

  const handleExport = () => {
    if (!exportWidgetCsv({ title, payload })) onExport?.(id, "csv")
  }

  return (
    <Space size={widgetActionGap} data-testid="widget-actions">
      {caps.includes("refresh") && (
        <ActionTooltip title={t("widget.action.refresh", "Refresh")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.refresh", "Refresh")}
            data-testid="widget-refresh"
            icon={<ReloadOutlined />}
            onClick={() => onRefresh?.(id)}
          />
        </ActionTooltip>
      )}
      {caps.includes("expand") && (
        <ActionTooltip title={t("widget.action.expand", "Expand")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.expand", "Expand")}
            data-testid="widget-expand"
            icon={<FullscreenOutlined />}
            onClick={() => onExpand?.(id)}
          />
        </ActionTooltip>
      )}
      {caps.includes("export") && (
        <ActionTooltip title={t("widget.action.export", "Export")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.export", "Export")}
            data-testid="widget-export"
            icon={<DownloadOutlined />}
            onClick={handleExport}
          />
        </ActionTooltip>
      )}
      {caps.includes("drill") && Boolean(drillTarget) && (
        <ActionTooltip title={t("widget.action.drill", "Open")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.drill", "Open")}
            data-testid="widget-drill"
            icon={<ArrowRightOutlined />}
            onClick={() => onDrill?.(drillTarget ?? "")}
          />
        </ActionTooltip>
      )}
    </Space>
  )
}

export const WidgetFrame = ({
  title,
  state = "ready",
  capabilities,
  widgetId,
  bare,
  emptyText,
  errorText,
  drillTarget,
  payload,
  onRefresh,
  onExpand,
  onExport,
  onDrill,
  onRetry,
  editChrome,
  children
}: WidgetFrameProps) => {
  const { t } = useTranslation()

  if (bare) return <>{children}</>

  let body: React.ReactNode
  if (state === "loading") {
    body = (
      <Skeleton active data-testid="widget-loading" paragraph={{ rows: 2 }} />
    )
  } else if (state === "error") {
    body = (
      <Flex
        vertical
        align="center"
        gap={8}
        data-testid="widget-error"
        style={{ padding: 16 }}
      >
        <span style={{ color: neutral.muted }}>
          {errorText ?? t("widget.error.title", "Something went wrong")}
        </span>
        <Button
          size="small"
          onClick={() => (onRetry ? onRetry() : onRefresh?.(widgetId ?? ""))}
        >
          {t("widget.error.retry", "Retry")}
        </Button>
      </Flex>
    )
  } else if (state === "empty") {
    body = (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={emptyText ?? t("widget.empty", "No data")}
      />
    )
  } else {
    body = children
  }

  return (
    <Card
      size="small"
      title={title}
      style={frameStyle}
      extra={
        <Flex align="center" gap={4}>
          <FrameActions
            capabilities={capabilities}
            widgetId={widgetId}
            title={title}
            drillTarget={drillTarget}
            payload={payload}
            onRefresh={onRefresh}
            onExpand={onExpand}
            onExport={onExport}
            onDrill={onDrill}
          />
          {editChrome}
        </Flex>
      }
    >
      {body}
    </Card>
  )
}
